import type {
  CrowdsecBouncerHealth,
  CrowdsecHealthReport,
  CrowdsecHubItem,
  CrowdsecScenarioHealth,
  CrowdsecSourceHealth,
  CrowdsecWarning,
} from '@bartizo/shared';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { getCrowdsecHealth } from '../api/servers.js';
import { formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Card } from '../ui/Card.js';
import { DataTable, type Column } from '../ui/DataTable.js';

/**
 * CrowdSec's own health on a server (P11.9.1; knowledge doc section 8.1).
 *
 * A CrowdSec that runs is not one that works. This answers the two questions
 * behind every ban: is it reading this server's logs — and understanding them —
 * and is something enforcing what it decides? Then the hub's state, and which
 * scenarios have fired.
 *
 * Everything here is the agent's: it holds the counters over time and decides
 * what is wrong, so a log "not parsing" or a bouncer "silent" is read from the
 * report's warnings rather than worked out again in the browser.
 */

const count = (value: number): string => value.toLocaleString('en-US');

const warningFor = (
  warnings: readonly CrowdsecWarning[],
  code: CrowdsecWarning['code'],
  subject: string,
): CrowdsecWarning | undefined =>
  warnings.find((warning) => warning.code === code && warning.subject === subject);

export function CrowdsecHealthCard({
  serverId,
  installed,
  timezone,
}: {
  serverId: string;
  /** `capabilities.crowdsec.installed`; nothing is drawn unless it is true. */
  installed: boolean | undefined;
  timezone: string;
}): ReactNode {
  const health = useQuery({
    queryKey: ['crowdsec-health', serverId],
    queryFn: () => getCrowdsecHealth(serverId),
    enabled: installed === true,
    refetchInterval: 60_000,
  });

  if (installed !== true) return null;

  const report = health.data?.report ?? null;
  const problems = report?.warnings.filter((warning) => warning.level === 'warning') ?? [];

  return (
    <Card
      title="CrowdSec health"
      description={
        report === null
          ? 'Whether CrowdSec reads and understands this server’s logs, and whether its bans are enforced.'
          : `As the agent saw it ${formatRelative(report.takenAt)}${report.version === null ? '' : `, CrowdSec ${report.version}`}.`
      }
      aside={
        report !== null &&
        (problems.length === 0 ? (
          <span className="badge badge-success">Working</span>
        ) : (
          <span className="badge badge-warning">
            {problems.length === 1 ? '1 problem' : `${String(problems.length)} problems`}
          </span>
        ))
      }
    >
      {health.isError && (
        <Alert tone="danger" title="Could not load CrowdSec’s health">
          Try again in a moment.
        </Alert>
      )}
      {health.data !== undefined && report === null && (
        <p className="muted text-sm">
          Nothing reported yet. The agent checks CrowdSec every minute once it and its root helper
          are up to date; running the installer again on this server updates both.
        </p>
      )}
      {report !== null && <HealthReport report={report} timezone={timezone} />}
    </Card>
  );
}

function HealthReport({
  report,
  timezone,
}: {
  report: CrowdsecHealthReport;
  timezone: string;
}): ReactNode {
  const warnings = report.warnings.filter((warning) => warning.level === 'warning');
  const notices = report.warnings.filter((warning) => warning.level === 'notice');
  const minutes = Math.max(1, Math.round(report.windowSeconds / 60));
  const recent = report.windowSeconds === 0 ? 'since it started' : `last ${String(minutes)} min`;

  const sourceColumns: Column<CrowdsecSourceHealth>[] = [
    {
      key: 'source',
      header: 'Log',
      rowHeader: true,
      cell: (row) => {
        const unparsed = warningFor(report.warnings, 'source_unparsed', row.source);
        const unread = warningFor(report.warnings, 'bartizo_log_unread', row.source);
        return (
          <>
            <code className="secret-text">{row.source}</code>
            <span className="cell-sub">
              {row.label ?? 'no type'} · {row.datasource}
              {unparsed !== undefined && (
                <>
                  {' '}
                  <span className="badge badge-warning">Not parsing</span>
                </>
              )}
              {unread !== undefined && (
                <>
                  {' '}
                  <span className="badge badge-warning">Not read</span>
                </>
              )}
            </span>
          </>
        );
      },
      sortValue: (row) => row.source,
    },
    {
      key: 'recentRead',
      header: `Read (${recent})`,
      align: 'end',
      cell: (row) => count(row.recentRead),
      sortValue: (row) => row.recentRead,
    },
    {
      key: 'recentParsed',
      header: 'Parsed',
      align: 'end',
      cell: (row) => count(row.recentParsed),
      sortValue: (row) => row.recentParsed,
    },
    {
      key: 'recentUnparsed',
      header: 'Not parsed',
      align: 'end',
      cell: (row) => count(row.recentUnparsed),
      sortValue: (row) => row.recentUnparsed,
    },
    {
      key: 'read',
      header: 'Read since start',
      align: 'end',
      wideOnly: true,
      cell: (row) => count(row.read),
      sortValue: (row) => row.read,
    },
  ];

  const bouncerColumns: Column<CrowdsecBouncerHealth>[] = [
    {
      key: 'name',
      header: 'Bouncer',
      rowHeader: true,
      cell: (row) => (
        <>
          {row.name}
          {(row.type !== null || row.version !== null) && (
            <span className="cell-sub">
              {[row.type, row.version].filter((part) => part !== null).join(' ')}
            </span>
          )}
        </>
      ),
      sortValue: (row) => row.name,
    },
    {
      key: 'state',
      header: 'State',
      cell: (row) =>
        row.revoked ? (
          <span className="badge badge-muted">Revoked</span>
        ) : warningFor(report.warnings, 'bouncer_silent', row.name) !== undefined ? (
          <span className="badge badge-danger">Silent</span>
        ) : (
          <span className="badge badge-success">Pulling</span>
        ),
    },
    {
      key: 'lastPull',
      header: 'Last pull',
      cell: (row) =>
        row.lastPull === null ? (
          <span className="muted">Never</span>
        ) : (
          <>
            {formatRelative(row.lastPull)}
            <span className="cell-sub">{formatInstant(row.lastPull, { timezone })}</span>
          </>
        ),
      sortValue: (row) => row.lastPull ?? '',
    },
  ];

  const scenarioColumns: Column<CrowdsecScenarioHealth>[] = [
    {
      key: 'name',
      header: 'Scenario',
      rowHeader: true,
      cell: (row) => row.name,
      sortValue: (row) => row.name,
    },
    {
      key: 'overflowed',
      header: 'Fired',
      align: 'end',
      cell: (row) => count(row.overflowed),
      sortValue: (row) => row.overflowed,
    },
    {
      key: 'poured',
      header: 'Events seen',
      align: 'end',
      cell: (row) => count(row.poured),
      sortValue: (row) => row.poured,
    },
  ];

  return (
    <>
      {warnings.map((warning) => (
        <Alert key={`${warning.code}|${warning.subject ?? ''}`} tone="warning">
          {warning.message}
        </Alert>
      ))}
      {notices.length > 0 && (
        <ul className="text-sm muted">
          {notices.map((notice) => (
            <li key={`${notice.code}|${notice.subject ?? ''}`}>{notice.message}</li>
          ))}
        </ul>
      )}

      <section>
        <h3>Logs it reads</h3>
        {report.metrics.state === 'ok' ? (
          <DataTable
            caption="Logs CrowdSec reads, and how many of their lines it understood"
            columns={sourceColumns}
            rows={report.sources}
            rowKey={(row) => row.source}
            initialSort={{ key: 'recentRead', direction: 'desc' }}
            empty={<p className="muted text-sm">CrowdSec has read nothing since it started.</p>}
          />
        ) : (
          <p className="muted text-sm">
            {report.metrics.state === 'disabled'
              ? 'CrowdSec’s metrics are switched off, so what it reads cannot be shown.'
              : report.metrics.state === 'unreachable'
                ? 'CrowdSec’s metrics did not answer.'
                : 'Not checked yet.'}
          </p>
        )}
      </section>

      <section>
        <h3>What enforces its bans</h3>
        {report.bouncers === null ? (
          <p className="muted text-sm">Not checked yet: the root helper has not answered.</p>
        ) : (
          <DataTable
            caption="Bouncers registered with CrowdSec"
            columns={bouncerColumns}
            rows={report.bouncers}
            rowKey={(row) => row.name}
            empty={<p className="muted text-sm">No bouncer is registered.</p>}
          />
        )}
      </section>

      {report.scenarios.some((scenario) => scenario.overflowed > 0) && (
        <section>
          <h3>Scenarios that fired since CrowdSec started</h3>
          <DataTable
            caption="Scenarios by how often they fired"
            columns={scenarioColumns}
            rows={report.scenarios.filter((scenario) => scenario.overflowed > 0).slice(0, 15)}
            rowKey={(row) => row.name}
            initialSort={{ key: 'overflowed', direction: 'desc' }}
          />
        </section>
      )}

      {report.hub !== null && <HubSummary hub={report.hub} />}

      {report.inspectedAt !== null && (
        <p className="text-sm muted">
          Bouncers and hub checked {formatRelative(report.inspectedAt)}; logs every minute.
        </p>
      )}
    </>
  );
}

function HubSummary({ hub }: { hub: NonNullable<CrowdsecHealthReport['hub']> }): ReactNode {
  const flagged = [...hub.collections, ...hub.problems].filter(
    (item) => item.tainted || item.outdated,
  );
  return (
    <section>
      <h3>The hub</h3>
      <p className="text-sm">
        {count(hub.installed)} items installed · {count(hub.tainted)} edited here ·{' '}
        {count(hub.outdated)} with a newer version · {count(hub.local)} of the server’s own
      </p>
      {hub.collections.length > 0 && (
        <p className="badge-list" aria-label="Installed collections">
          {hub.collections.map((item) => (
            <span key={item.name} className={`badge ${badgeOf(item)}`} title={titleOf(item)}>
              {item.name}
              {item.version === null ? '' : ` ${item.version}`}
            </span>
          ))}
        </p>
      )}
      {flagged.length > 0 && (
        <ul className="text-sm">
          {flagged.slice(0, 10).map((item) => (
            <li key={`${item.kind}/${item.name}`}>
              <code>{item.name}</code> ({item.kind}) — {titleOf(item)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const badgeOf = (item: CrowdsecHubItem): string =>
  item.tainted ? 'badge-warning' : item.outdated ? 'badge-accent' : 'badge-muted';

const titleOf = (item: CrowdsecHubItem): string =>
  item.tainted
    ? 'edited on this server; the hub’s updates no longer reach it'
    : item.outdated
      ? 'a newer version is in the hub'
      : item.local
        ? 'a file of this server’s own'
        : 'up to date';
